import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Address } from '../../types';

type AddressTypeSelectorProps = {
    value: Address['type'];
    onChange: (value: Address['type']) => void;
};

const options: Array<{
    value: Address['type'];
    label: string;
    icon: string;
}> = [
    {
        value: 'home',
        label: 'خانه',
        icon: 'fa-light fa-house',
    },
    {
        value: 'company',
        label: 'محل کار',
        icon: 'fa-light fa-building',
    },
];

const AddressTypeSelector = ({ value, onChange }: AddressTypeSelectorProps) => {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 1.25,
                width: '100%',
            }}
        >
            {options.map((option) => {
                const selected = value === option.value;

                return (
                    <ButtonBase
                        key={option.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onChange(option.value)}
                        sx={(theme) => ({
                            minWidth: 0,
                            minHeight: 58,
                            px: { xs: 1.5, sm: 2 },
                            py: 1.25,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: selected
                                ? 'primary.main'
                                : 'divider',
                            backgroundColor: selected
                                ? alpha(
                                      theme.palette.primary.main,
                                      theme.palette.mode === 'dark'
                                          ? 0.14
                                          : 0.055,
                                  )
                                : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            transition:
                                'border-color .18s ease, background-color .18s ease, box-shadow .18s ease',
                            '&:hover': {
                                borderColor: selected
                                    ? 'primary.main'
                                    : alpha(theme.palette.primary.main, 0.45),
                                backgroundColor: selected
                                    ? alpha(
                                          theme.palette.primary.main,
                                          theme.palette.mode === 'dark'
                                              ? 0.18
                                              : 0.075,
                                      )
                                    : alpha(theme.palette.primary.main, 0.035),
                            },
                        })}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 0,
                            }}
                        >
                            <Box
                                component="i"
                                className={option.icon}
                                sx={{
                                    color: selected
                                        ? 'primary.main'
                                        : 'text.secondary',
                                    fontSize: 18,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: selected ? 700 : 500,
                                }}
                            >
                                {option.label}
                            </Typography>
                        </Box>

                        <Box
                            sx={(theme) => ({
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: selected
                                    ? 'primary.main'
                                    : 'divider',
                                backgroundColor: selected
                                    ? 'primary.main'
                                    : 'transparent',
                                color: theme.palette.primary.contrastText,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            })}
                        >
                            {selected && (
                                <Box
                                    component="i"
                                    className="fa-solid fa-check"
                                    sx={{ fontSize: 11 }}
                                />
                            )}
                        </Box>
                    </ButtonBase>
                );
            })}
        </Box>
    );
};

export default AddressTypeSelector;
